import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  TrashIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

const EnquiryAdmin = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedMethod, setSelectedMethod] = useState("All");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const fetchEnquiries = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/enquiry/getAll`
      );
      setEnquiries(res.data.data || []);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/enquiry/delete/${id}`
      );
      setEnquiries((prev) => prev.filter((item) => item._id !== id));
      setDeleteTarget(null);
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  useEffect(() => {
    fetchEnquiries();
  }, []);

  // Filter enquiries based on search and method
  const filteredEnquiries = enquiries.filter((item) => {
    const matchesSearch = 
      item.name?.toLowerCase().includes(search.toLowerCase()) ||
      item.email?.toLowerCase().includes(search.toLowerCase()) ||
      item.number?.includes(search) ||
      item.query?.toLowerCase().includes(search.toLowerCase()) ||
      item.projectType?.toLowerCase().includes(search.toLowerCase());
    
    const matchesMethod = selectedMethod === "All" || item.method === selectedMethod;
    
    return matchesSearch && matchesMethod;
  });

  const methods = ["All", ...new Set(enquiries.map(e => e.method).filter(Boolean))];

  const today = new Date().toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });

  /* ---------------- Loading ---------------- */
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="w-12 h-12 sm:w-14 sm:h-14 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  /* ---------------- UI ---------------- */
  return (
    <div className="p-4 sm:p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Enquiries</h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">Manage all customer enquiries</p>
        </div>

        <button
          onClick={fetchEnquiries}
          className="flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2.5 sm:px-4 sm:py-2 rounded-lg hover:bg-blue-700 transition-colors w-full sm:w-auto text-sm"
        >
          <ArrowPathIcon className="w-4 h-4 sm:w-5 sm:h-5" />
          <span>Refresh</span>
        </button>
      </div>

      {/* Search & Filter Bar */}
      <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4 mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className="flex-1 flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 border border-gray-200">
            <MagnifyingGlassIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, phone, or query..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 bg-transparent outline-none text-sm text-gray-700 placeholder-gray-400"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Method Filter - Desktop */}
          <div className="hidden sm:flex items-center gap-2">
            <span className="text-sm text-gray-500">Method:</span>
            <select
              value={selectedMethod}
              onChange={(e) => setSelectedMethod(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-400 bg-white"
            >
              {methods.map(method => (
                <option key={method} value={method}>{method}</option>
              ))}
            </select>
          </div>

          {/* Mobile Filter Toggle */}
          <button
            onClick={() => setMobileFilterOpen(!mobileFilterOpen)}
            className="sm:hidden flex items-center justify-between w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50"
          >
            <span className="text-gray-600">Filter by: {selectedMethod}</span>
            <span className="text-gray-400">▼</span>
          </button>
        </div>

        {/* Mobile Filter Menu */}
        {mobileFilterOpen && (
          <div className="sm:hidden mt-3 pt-3 border-t border-gray-100">
            <p className="text-xs font-medium text-gray-500 mb-2">Filter by method:</p>
            <div className="flex flex-wrap gap-2">
              {methods.map(method => (
                <button
                  key={method}
                  onClick={() => {
                    setSelectedMethod(method);
                    setMobileFilterOpen(false);
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex-1 ${
                    selectedMethod === method
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {method}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden lg:block bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="p-4 text-left font-semibold">User</th>
                <th className="p-4 text-left font-semibold">Contact</th>
                <th className="p-4 text-left font-semibold">Project</th>
                <th className="p-4 text-left font-semibold">Query</th>
                <th className="p-4 text-left font-semibold">Method</th>
                <th className="p-4 text-right font-semibold">Action</th>
              </tr>
            </thead>

            <tbody>
              {filteredEnquiries.map((item, index) => (
                <tr 
                  key={item._id} 
                  className={`border-t border-gray-100 hover:bg-gray-50 transition-colors ${
                    index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                  }`}
                >
                  {/* User */}
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-blue-100 p-1.5 rounded-full flex-shrink-0">
                        <UserIcon className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                      </div>
                      <span className="font-medium text-gray-900">{item.name}</span>
                    </div>
                  </td>

                  {/* Contact */}
                  <td className="p-4">
                    <div className="flex items-center gap-1.5 text-sm">
                      <EnvelopeIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="truncate max-w-[150px]" title={item.email}>
                        {item.email}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 text-sm text-gray-500 mt-1">
                      <PhoneIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span>{item.number}</span>
                    </div>
                  </td>

                  {/* Project */}
                  <td className="p-4">
                    <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium">
                      {item.projectType || "—"}
                    </span>
                  </td>

                  {/* Query */}
                  <td className="p-4 max-w-xs">
                    <p className="text-sm text-gray-600 line-clamp-2" title={item.query}>
                      {item.query}
                    </p>
                  </td>

                  {/* Method */}
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-lg text-xs font-medium ${
                      item.method === 'phone' 
                        ? 'bg-green-50 text-green-700'
                        : item.method === 'email'
                        ? 'bg-purple-50 text-purple-700'
                        : 'bg-gray-50 text-gray-700'
                    }`}>
                      {item.method || "—"}
                    </span>
                  </td>

                  {/* Action */}
                  <td className="p-4 text-right">
                    <button
                      onClick={() => setDeleteTarget(item)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <TrashIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {filteredEnquiries.length === 0 && (
          <div className="text-center py-16 text-gray-500">
            <div className="text-4xl mb-3">📭</div>
            <p className="font-medium text-gray-600">No enquiries found</p>
            <p className="text-sm mt-1 text-gray-400">
              {search || selectedMethod !== "All" 
                ? "Try adjusting your filters" 
                : "Enquiries will appear here"}
            </p>
          </div>
        )}
      </div>

      {/* Mobile/Tablet Card View */}
      <div className="lg:hidden space-y-3 sm:space-y-4">
        {filteredEnquiries.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 py-16 text-center text-gray-500">
            <div className="text-4xl mb-3">📭</div>
            <p className="font-medium text-gray-600">No enquiries found</p>
            <p className="text-sm mt-1 text-gray-400">
              {search || selectedMethod !== "All" 
                ? "Try adjusting your filters" 
                : "Enquiries will appear here"}
            </p>
          </div>
        ) : (
          filteredEnquiries.map((item) => (
            <div
              key={item._id}
              className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 hover:shadow-md transition-shadow"
            >
              {/* Header with Name and Action */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="bg-blue-100 p-2 rounded-full flex-shrink-0">
                    <UserIcon className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-gray-900 text-base sm:text-lg truncate">
                      {item.name}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`px-2 py-0.5 rounded-lg text-xs font-medium ${
                        item.method === 'phone' 
                          ? 'bg-green-50 text-green-700'
                          : item.method === 'email'
                          ? 'bg-purple-50 text-purple-700'
                          : 'bg-gray-50 text-gray-700'
                      }`}>
                        {item.method || "—"}
                      </span>
                      <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium">
                        {item.projectType || "General"}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setDeleteTarget(item)}
                  className="p-2.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors ml-2 flex-shrink-0"
                  title="Delete"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Contact Info */}
              <div className="space-y-2 mb-3 pl-2">
                <div className="flex items-center gap-2 text-sm">
                  <EnvelopeIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <a href={`mailto:${item.email}`} className="text-blue-600 hover:underline truncate">
                    {item.email}
                  </a>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <PhoneIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <a href={`tel:${item.number}`} className="text-gray-700 hover:text-blue-600">
                    {item.number}
                  </a>
                </div>
              </div>

              {/* Query */}
              <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                <p className="text-xs font-medium text-gray-500 mb-1">Query:</p>
                <p className="text-sm text-gray-700 leading-relaxed">
                  {item.query}
                </p>
              </div>

              {/* Footer Metadata */}
              <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-400">
                <span>ID: {item._id.slice(-6)}</span>
                <span>{new Date(item.createdAt).toLocaleDateString() || today}</span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer Stats */}
      <div className="mt-4 sm:mt-6 px-4 sm:px-6 py-3 sm:py-4 bg-white rounded-xl border border-gray-200 flex flex-wrap items-center justify-between gap-3 text-xs sm:text-sm text-gray-500">
        <span>Total: <strong className="text-gray-800">{enquiries.length}</strong></span>
        <span>Showing: <strong className="text-blue-600">{filteredEnquiries.length}</strong></span>
        <span className="hidden sm:inline">Methods: <strong className="text-gray-800">{methods.length - 1}</strong></span>
        <span className="hidden md:inline">Last Updated: <strong className="text-gray-800">{today}</strong></span>
        
        {/* Mobile stats */}
        <div className="flex sm:hidden items-center gap-3 w-full justify-center border-t border-gray-100 pt-2 mt-1">
          <span>Methods: <strong className="text-gray-800">{methods.length - 1}</strong></span>
          <span>Updated: <strong className="text-gray-800">{today}</strong></span>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div
          className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4"
          onClick={() => setDeleteTarget(null)}
        >
          <div
            className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-sm shadow-2xl p-6 text-center animate-slide-up sm:animate-none"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center text-2xl mx-auto mb-4">
              🗑️
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Enquiry?</h3>
            <p className="text-sm text-gray-500 mb-6">
              You're about to delete the enquiry from{" "}
              <strong className="text-gray-800">{deleteTarget.name}</strong>. 
              This action cannot be undone.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 px-4 py-3 sm:py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition order-2 sm:order-1"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteTarget._id)}
                className="flex-1 px-4 py-3 sm:py-2.5 text-sm font-semibold bg-red-500 hover:bg-red-600 text-white rounded-lg transition order-1 sm:order-2"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add custom animation styles */}
      <style jsx>{`
        @keyframes slideUp {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default EnquiryAdmin;